import { useEffect, useState } from "react";

/**
 * Hook to detect keyboard height and state via the Visual Viewport API.
 *
 * iOS (WKWebView) / Desktop: the window does NOT resize when the keyboard opens,
 * so `innerHeight − visualViewport.height` is the keyboard height and consumers
 * (KeyboardAvoidingView) shrink the layout to sit above it.
 *
 * Android (WebView) uses `adjustResize` (AndroidManifest), so the OS already
 * resizes the WebView when the keyboard opens: `visualViewport.height` tracks the
 * shrunk window, the diff stays ~0, and this hook reports the keyboard as closed.
 * That is intentional — the OS handles avoidance, and consumers become no-ops.
 * (A previous native bridge that injected `window.__nativeKeyboardHeight` was
 * removed: combined with `adjustResize` it subtracted the keyboard height twice
 * and collapsed the content area on some devices, e.g. Samsung S20FE.)
 */
export function useKeyboardHeight(): {
  keyboardHeight: number;
  isKeyboardOpen: boolean;
} {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  useEffect(() => {
    function updateKeyboardState(): void {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        const newHeight = Math.max(heightDiff, 0);
        setKeyboardHeight(newHeight);
        setIsKeyboardOpen(heightDiff > 150);
      }
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateKeyboardState);
      window.visualViewport.addEventListener("scroll", updateKeyboardState);
    }

    // Initial check
    updateKeyboardState();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          "resize",
          updateKeyboardState
        );
        window.visualViewport.removeEventListener(
          "scroll",
          updateKeyboardState
        );
      }
    };
  }, []);

  return { keyboardHeight, isKeyboardOpen };
}
